import React from 'react';
import { Link } from 'react-router-dom';
import './InternalLinksEngine.css';

// Intelligent internal linking engine for maximum SEO juice distribution
const InternalLinksEngine = ({ currentSound, currentCategory, currentSubcategory, sounds = [] }) => {
  
  // Strategic link groups for maximum SEO impact
  const linkStrategies = {
    // Related sounds by theme
    getRelatedSounds: (sound, limit = 6) => {
      if (!sound) return [];
      
      return sounds
        .filter(s => s.theme === sound.theme && s.title !== sound.title)
        .slice(0, limit)
        .map(s => ({
          url: `/sons/${generateSlug(s.title)}`,
          text: `${s.title} gratuit`,
          anchor: `Télécharger ${s.title} en MP3`,
          type: 'related-sound'
        }));
    },

    // Category cross-linking
    getCategoryLinks: (currentCat, currentSub) => {
      const categoryMap = {
        'nature': {
          subcategories: ['vent', 'eau', 'oiseaux', 'animaux', 'feuillage', 'orage'],
          related: ['ambiance', 'effets-sonores']
        },
        'ambiance': {
          subcategories: ['ville', 'cafe', 'bureau', 'nuit', 'foule', 'halloween'],
          related: ['nature', 'effets-sonores']
        },
        'effets-sonores': {
          subcategories: ['explosion', 'impact', 'swoosh', 'cartoon', 'transition', 'metal'],
          related: ['ambiance', 'nature']
        }
      };

      const links = [];
      const catData = categoryMap[currentCat];
      
      if (catData) {
        // Subcategory links
        catData.subcategories
          .filter(sub => sub !== currentSub)
          .slice(0, 4)
          .forEach(sub => {
            links.push({
              url: `/category/${currentCat}/${sub}`,
              text: `Sons ${sub} ${currentCat}`,
              anchor: `Découvrir les sons ${sub} gratuits`,
              type: 'subcategory'
            });
          });

        // Related category links
        catData.related.forEach(relCat => {
          links.push({
            url: `/category/${relCat}`,
            text: `Sons ${relCat} gratuits`,
            anchor: `Explorer la catégorie ${relCat}`,
            type: 'category'
          });
        });
      }

      return links;
    },

    // High-value landing page links
    getLandingPageLinks: () => [
      {
        url: '/sons-gratuits-mp3-wav',
        text: 'Sons gratuits MP3 WAV',
        anchor: 'Tous nos sons gratuits en MP3 et WAV',
        type: 'landing'
      },
      {
        url: '/effets-sonores-libres-droits',
        text: 'Effets sonores libres de droits',
        anchor: 'Collection complète d\'effets sonores gratuits',
        type: 'landing'
      },
      {
        url: '/ambiances-sonores-gratuites',
        text: 'Ambiances sonores gratuites',
        anchor: 'Ambiances et atmosphères audio gratuites',
        type: 'landing'
      }
    ],

    // Trending/popular content links
    getTrendingLinks: () => [
      {
        url: '/sons-populaires',
        text: 'Sons les plus populaires',
        anchor: 'Découvrir les sons les plus téléchargés',
        type: 'trending'
      },
      {
        url: '/nouveaux-sons',
        text: 'Nouveaux sons ajoutés',
        anchor: 'Derniers sons ajoutés à la collection',
        type: 'new'
      }
    ]
  };

  // Generate SEO-optimized slug
  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim('-');
  };

  // Contextual link recommendations based on page type
  const getContextualLinks = () => {
    const links = [];

    // For sound pages
    if (currentSound) {
      links.push(...linkStrategies.getRelatedSounds(currentSound, 4));
      links.push(...linkStrategies.getCategoryLinks(currentSound.theme));
    }

    // For category pages
    if (currentCategory) {
      links.push(...linkStrategies.getCategoryLinks(currentCategory, currentSubcategory));
    }

    // Always include some high-value links
    links.push(...linkStrategies.getLandingPageLinks().slice(0, 2));
    
    return links.slice(0, 8); // Limit to 8 links max for optimal UX
  };

  const contextualLinks = getContextualLinks();

  if (contextualLinks.length === 0) return null;

  return (
    <aside className="internal-links-engine" role="complementary">
      <h3 className="internal-links-title">Découvrez aussi</h3>
      <nav className="internal-links-nav" aria-label="Liens connexes">
        <ul className="internal-links-list">
          {contextualLinks.map((link, index) => (
            <li key={index} className={`internal-link-item ${link.type}`}>
              <Link 
                to={link.url}
                className="internal-link"
                title={link.anchor}
                rel="nofollow" // Prevent link juice dilution
              >
                <span className="link-text">{link.text}</span>
                <span className="link-arrow">→</span>
              </Link>
            </li>
          ))}
        </ul>
      </nav>
      
      {/* Contextual call-to-action */}
      <div className="internal-links-cta">
        <Link to="/favorites" className="cta-link">
          ❤️ Sauvegarder vos sons préférés
        </Link>
      </div>
    </aside>
  );
};

// Breadcrumb component with rich structured data
export const SEOBreadcrumbs = ({ items = [] }) => {
  if (items.length === 0) return null;

  return (
    <nav className="seo-breadcrumbs" aria-label="Fil d'Ariane">
      <ol className="breadcrumb-list" itemScope itemType="https://schema.org/BreadcrumbList">
        {items.map((item, index) => (
          <li 
            key={index}
            className="breadcrumb-item"
            itemProp="itemListElement"
            itemScope
            itemType="https://schema.org/ListItem"
          >
            {index < items.length - 1 ? (
              <Link 
                to={item.url}
                itemProp="item"
                className="breadcrumb-link"
              >
                <span itemProp="name">{item.name}</span>
              </Link>
            ) : (
              <span itemProp="name" className="breadcrumb-current">
                {item.name}
              </span>
            )}
            <meta itemProp="position" content={index + 1} />
            {index < items.length - 1 && (
              <span className="breadcrumb-separator" aria-hidden="true"> / </span>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
};

// Related content recommendations with SEO optimization
export const RelatedContent = ({ currentSound, sounds = [] }) => {
  if (!currentSound || sounds.length === 0) return null;

  // Get sounds from same theme
  const relatedByTheme = sounds
    .filter(s => s.theme === currentSound.theme && s.title !== currentSound.title)
    .slice(0, 4);

  // Get sounds with similar tags
  const relatedByTags = sounds
    .filter(s => {
      if (s.title === currentSound.title) return false;
      const commonTags = s.tags?.filter(tag => 
        currentSound.tags?.includes(tag)
      ) || [];
      return commonTags.length >= 2;
    })
    .slice(0, 4);

  const allRelated = [...relatedByTheme, ...relatedByTags]
    .filter((sound, index, self) => 
      index === self.findIndex(s => s.title === sound.title)
    )
    .slice(0, 6);

  if (allRelated.length === 0) return null;

  return (
    <section className="related-content" aria-labelledby="related-heading">
      <h2 id="related-heading" className="related-title">
        Sons similaires à {currentSound.title}
      </h2>
      <div className="related-grid">
        {allRelated.map((sound) => {
          const slug = generateSlug(sound.title);
          return (
            <article key={sound.title} className="related-item">
              <h3 className="related-item-title">
                <Link 
                  to={`/sons/${slug}`}
                  className="related-item-link"
                  title={`Écouter ${sound.title} gratuit`}
                >
                  {sound.title}
                </Link>
              </h3>
              <p className="related-item-meta">
                <span className="related-theme">{sound.theme}</span>
                <span className="related-duration">{sound.duration}</span>
              </p>
            </article>
          );
        })}
      </div>
    </section>
  );
};

// Generate SEO slug helper
const generateSlug = (title) => {
  return title
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .trim('-');
};

export default InternalLinksEngine;
